import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getKioskStartRegistrationDataRequest } from 'api/requests/kiosk-session-requests';
import { fromResponseToKioskStartRegistrationData } from 'mappers/kiosk-start-registration-data-mapper';
import kioskSessionService from 'services/kiosk-session-service';
import { TKioskStartRegistrationData } from 'types/kiosk/TKioskStartRegistrationData';
import { pickKioskPolicies } from 'utils/kiosk-language';
import { useEndKioskSession } from './useEndKioskSession';

export type TKioskStartRegStatus = 'idle' | 'loading' | 'ready' | 'no-planning' | 'unavailable' | 'error';

/**
 * Fetches the start-registration data for the tapped employee.
 * 401 → dead session (gate drops to INACTIVE). 403 EMPLOYEE_NOT_AT_LOCATION → 'unavailable'
 * (caller closes the modal + reloads the feed). 400 NO_ELIGIBLE_PLANNINGS → 'no-planning'
 * (the employee has nothing selectable today). Any other failure → 'error'.
 * Passing employeeId = null resets the hook (used while the modal is closed).
 */
export const useKioskStartRegistrationData = (employeeId: number | null) => {
  const endSession = useEndKioskSession();
  const [data, setData] = useState<TKioskStartRegistrationData | null>(null);
  const [status, setStatus] = useState<TKioskStartRegStatus>('idle');
  const isMounted = useRef(true);
  const { i18n } = useTranslation();

  const fetchData = useCallback(
    async (id: number) => {
      const token = kioskSessionService.getToken();
      if (!token) {
        endSession();
        return;
      }
      setStatus('loading');
      setData(null);
      try {
        const body = await getKioskStartRegistrationDataRequest(token, id);
        if (!isMounted.current) return;
        const mapped = fromResponseToKioskStartRegistrationData(body);
        setData(mapped);
        setStatus('ready');
      } catch (e) {
        if (!isMounted.current) return;
        const httpStatus = (e as { response?: { status?: number } })?.response?.status;
        const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
        if (httpStatus === 401) {
          endSession();
          return;
        }
        if (httpStatus === 403 && code.includes('EMPLOYEE_NOT_AT_LOCATION')) {
          setStatus('unavailable');
          return;
        }
        setStatus(httpStatus === 400 && code.includes('NO_ELIGIBLE_PLANNINGS') ? 'no-planning' : 'error');
      }
    },
    [endSession],
  );

  useEffect(() => {
    isMounted.current = true;
    if (employeeId != null) {
      fetchData(employeeId);
    } else {
      setData(null);
      setStatus('idle');
    }
    return () => {
      isMounted.current = false;
    };
  }, [employeeId, fetchData]);

  // The policy text comes in every kiosk language, so a language switch re-renders it from `data` in place:
  // no request, and `data` stays the same object, so the view's form prefill does not re-run.
  const policies = useMemo(
    () => (data ? pickKioskPolicies(data.policiesByLanguage, data.policies, i18n.language) : []),
    [data, i18n.language],
  );

  return { data, status, policies };
};
